import { baseApi } from '../baseApi';

// --- Response types ---

export interface Practice {
  clinicId: string;
  name: string;
  role: string;
  practiceStatus?: string;
  ehrSystem?: string;
  ehrSystemOther?: string;
  integrationMethod?: string;
  dataReminderEmail?: string;
  dataReminderPhone?: string;
  dataRemindersEnabled?: boolean;
  dataRemindersExcludeWeekends?: boolean;
  /**
   * The practice-wide IANA timezone, from the practice `METADATA` row
   * (`pk = CLINIC#<clinicId>`, `sk = METADATA` in `medrender-clinics-${env}`).
   * That row is the source of truth; every shipped server-side reader reads it.
   *
   * The attribute name is `reminderTimezone` for compatibility only — it is NOT
   * reminder-scoped. It decides "today" for worklists, metrics, alarms,
   * computed metrics, visit alarms, reports, and billing collections. The
   * rename is deliberately not done: 22 non-test `api-integration` files and 13
   * non-test frontend files read this attribute, which makes a rename a
   * live-attribute migration (dual-read window, backfill, rollback story)
   * rather than a refactor.
   *
   * OPTIONAL BECAUSE ABSENCE IS MEANINGFUL: `undefined` means the practice has
   * stated no timezone, and read paths then resolve in UTC per the
   * Timezone_Fallback_Rule. `undefined` and `'UTC'` are different answers and
   * must not be collapsed on any read. The server read path (`getUserClinics`
   * in `medrender-api-onboarding/src/clinicService.js`) is written to omit this
   * key rather than default it, for exactly that reason.
   */
  reminderTimezone?: string;
  logoUrl?: string;
}

export interface Location {
  locationId: string;
  name: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  status: string;
}

export interface CreatePracticeRequest {
  clinicName: string;
  locationName: string;
  locationAddress1: string;
  locationAddress2?: string;
  locationCity: string;
  locationState: string;
  locationZip: string;
  locationPhone: string;
  ehrSystem: string;
  ehrSystemOther?: string;
  integrationMethod?: string;
  termsAcceptedAt: string;
  taxId: string;
  /**
   * IANA timezone string (e.g. "America/Chicago"). Defaults to 'UTC'
   * server-side if omitted — `clinicService.js` resolves
   * `resolvedTimezone = reminderTimezone || 'UTC'` on create. That default is
   * the write-path expression of the same Timezone_Fallback_Rule the read paths
   * follow (an absent, empty, malformed, or unreadable Practice_Timezone
   * resolves to UTC); an invalid value, by contrast, is rejected with a 400
   * rather than defaulted.
   *
   * `SetupPracticePage` always sends a value, so the default is no longer
   * reachable from our own onboarding form — but it stays reachable by any
   * other client or a direct API call, which is why the fallback is documented
   * rather than assumed dead.
   */
  reminderTimezone?: string;
}

export interface CreatePracticeResponse {
  success: boolean;
  clinicId: string;
  locationId: string;
}

export interface UpdatePracticeRequest {
  clinicId: string;
  [key: string]: unknown;
}

export interface AddLocationRequest {
  clinicId: string;
  name: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
}

export interface AddLocationResponse {
  success: boolean;
  locationId: string;
}

export interface UpdateLocationRequest {
  clinicId: string;
  locationId: string;
  [key: string]: unknown;
}

// --- Practice User Management types ---

export interface PracticeLookupRequest {
  taxId?: string;
  ownerEmail?: string;
}

export interface PracticeLookupResponse {
  clinicId: string;
  name: string;
}

export interface JoinPracticeResponse {
  memberId: string;
  status: string;
}

export interface PracticeMembership {
  clinicId: string;
  status: string;
  role: string;
  updatedAt?: string;
  providerMapping?: string | null;
}

export interface MembershipResponse {
  membership: PracticeMembership | null;
}

export interface PracticeMember {
  userId: string;
  email: string;
  name: string;
  role: string;
  status: string;
  memberId: string;
  createdAt: string;
  updatedAt: string;
  providerMapping?: string | null;
}

export interface PracticeMembersResponse {
  members: PracticeMember[];
}

export interface UpdateMemberRequest {
  clinicId: string;
  userId: string;
  action: 'approve' | 'reject' | 'change_role' | 'remove';
  role?: string;
}

export interface InviteMemberRequest {
  clinicId: string;
  email: string;
  role: string;
  providerMapping?: string;
  /**
   * Optional Group ids to assign the invited member to on accept (Req 13.1,
   * 13.4). The invite flows attach this only when one or more Active_Groups is
   * selected; with no selection the field is omitted entirely so the existing
   * invite contract is unchanged and no Invite_Group_Assignment is recorded.
   */
  groupIds?: string[];
}

// --- Practice Approval Workflow types ---

export interface PendingPractice {
  clinicId: string;
  name: string;
  taxId: string;
  ownerName: string;
  ownerEmail: string;
  createdAt: string;
}

export interface PendingPracticesResponse {
  practices: PendingPractice[];
}

export interface UpdatePracticeStatusRequest {
  clinicId: string;
  status: 'active' | 'rejected';
}

export interface UpdatePracticeStatusResponse {
  clinicId: string;
  status: string;
}

// --- Role Management types ---

export interface RoleDefinition {
  roleId: string;
  displayName: string;
  description: string;
  allowedRoutes: string[];
  navItems: { label: string; path: string; icon: string }[];
  defaultRedirect: string;
  assignableBy: string[];
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RolesResponse {
  roles: RoleDefinition[];
}

export interface AssignableRole {
  roleId: string;
  displayName: string;
  description: string;
}

export interface AssignableRolesResponse {
  roles: AssignableRole[];
}

export interface AssignRoleRequest {
  clinicId: string;
  userId: string;
  roleId: string;
}

export interface AdminCreateRoleRequest {
  roleId: string;
  displayName: string;
  description: string;
  allowedRoutes: string[];
  navItems: { label: string; path: string; icon: string }[];
  defaultRedirect: string;
  assignableBy: string[];
}

export interface AdminUpdateRoleRequest {
  roleId: string;
  displayName?: string;
  description?: string;
  allowedRoutes?: string[];
  navItems?: { label: string; path: string; icon: string }[];
  defaultRedirect?: string;
  assignableBy?: string[];
}

// --- Group Management types ---

export type GroupStatus = 'active' | 'archived';

/**
 * How a Group's membership is determined. `static` groups own explicit
 * Group_Membership records; the system-managed `Everyone` group is
 * `dynamic_all_active` and computes its members as the practice's current
 * Active_Members (it stores no membership records). See design §Data Models 4.1.
 */
export type GroupMembershipMode = 'static' | 'dynamic_all_active';

/**
 * A practice-controlled, practice-scoped grouping of users. The list endpoint
 * returns each Group with a `memberCount` computed by intersecting the group's
 * stored memberships with the practice's current Active_Members (the dynamic
 * `Everyone` group's count is the Active_Member count directly).
 */
export interface Group {
  groupId: string;
  clinicId: string;
  name: string;
  description?: string;
  status: GroupStatus;
  /** True for the four Default Groups and the Everyone group. */
  isDefault: boolean;
  /** True only for the system-protected Everyone group. */
  isSystem: boolean;
  membershipMode: GroupMembershipMode;
  /** Current member count (active members only). */
  memberCount: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

/**
 * A Group member resolved for display. The members listing endpoint returns
 * only userIds (`{ members: [...userId] }`); the Groups tab joins each userId
 * against the practice member list to obtain `name`/`email` for display.
 */
export interface GroupMember {
  userId: string;
  name: string;
  email: string;
  role?: string;
}

export interface GetGroupsRequest {
  clinicId: string;
  /** Include Archived_Groups in the list (default view excludes them). */
  includeArchived?: boolean;
}

export interface GetGroupMembersRequest {
  clinicId: string;
  groupId: string;
}

export interface CreateGroupRequest {
  clinicId: string;
  name: string;
  description?: string;
}

export interface UpdateGroupRequest {
  clinicId: string;
  groupId: string;
  name?: string;
  description?: string;
}

export interface ArchiveGroupRequest {
  clinicId: string;
  groupId: string;
}

export interface GroupMembershipRequest {
  clinicId: string;
  groupId: string;
  userId: string;
}

// --- Logo Upload types ---

export interface LogoPresignRequest {
  clinicId: string;
  fileName: string;
  contentType: string;
  fileSize: number;
}

export interface LogoPresignResponse {
  presignedUrl: string;
  s3Key: string;
  logoUrl: string;
}

export interface LogoConfirmRequest {
  clinicId: string;
  logoUrl: string;
}

export interface LogoConfirmResponse {
  logoUrl: string;
}

// --- RTK Query endpoints ---

const onboardingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPractice: builder.query<Practice[], void>({
      query: () => ({
        method: 'GET' as const,
        path: '/onboarding/practice',
      }),
    }),
    getPracticeLocations: builder.query<Location[], string>({
      query: (clinicId) => ({
        method: 'GET' as const,
        path: `/onboarding/practice/${clinicId}/locations`,
      }),
    }),
    createPractice: builder.mutation<CreatePracticeResponse, CreatePracticeRequest>({
      query: (body) => ({
        method: 'POST' as const,
        path: '/onboarding/practice',
        body,
      }),
    }),
    updatePractice: builder.mutation<Practice, UpdatePracticeRequest>({
      query: ({ clinicId, ...body }) => ({
        method: 'PUT' as const,
        path: `/onboarding/practice/${clinicId}`,
        body,
      }),
    }),
    addLocation: builder.mutation<AddLocationResponse, AddLocationRequest>({
      query: ({ clinicId, ...body }) => ({
        method: 'POST' as const,
        path: `/onboarding/practice/${clinicId}/locations`,
        body,
      }),
    }),
    updateLocation: builder.mutation<Location, UpdateLocationRequest>({
      query: ({ clinicId, locationId, ...body }) => ({
        method: 'PUT' as const,
        path: `/onboarding/practice/${clinicId}/locations/${locationId}`,
        body,
      }),
    }),
    practiceLookup: builder.mutation<PracticeLookupResponse, PracticeLookupRequest>({
      query: (body) => ({
        method: 'POST' as const,
        path: '/onboarding/practice/lookup',
        body,
      }),
    }),
    joinPractice: builder.mutation<JoinPracticeResponse, { clinicId: string; email?: string; name?: string }>({
      query: ({ clinicId, ...body }) => ({
        method: 'POST' as const,
        path: `/onboarding/practice/${clinicId}/join`,
        body,
      }),
    }),
    getMyMembership: builder.query<MembershipResponse, void>({
      query: () => ({
        method: 'GET' as const,
        path: '/onboarding/practice/my-membership',
      }),
    }),
    getPracticeMembers: builder.query<PracticeMembersResponse, string>({
      query: (clinicId) => ({
        method: 'GET' as const,
        path: `/onboarding/practice/${clinicId}/members`,
      }),
      providesTags: ['PracticeMembers'],
    }),
    updateProviderMapping: builder.mutation<
      void,
      { clinicId: string; userId: string; providerMapping: string | null }
    >({
      query: ({ clinicId, userId, providerMapping }) => ({
        method: 'PUT' as const,
        path: `/onboarding/practice/${clinicId}/members/${userId}/provider-mapping`,
        body: { providerMapping },
      }),
      invalidatesTags: ['PracticeMembers'],
    }),
    updatePracticeMember: builder.mutation<{ member: PracticeMember }, UpdateMemberRequest>({
      query: ({ clinicId, userId, ...body }) => ({
        method: 'PUT' as const,
        path: `/onboarding/practice/${clinicId}/members/${userId}`,
        body,
      }),
    }),
    invitePracticeMember: builder.mutation<{ memberId: string; status: string }, InviteMemberRequest>({
      query: ({ clinicId, ...body }) => ({
        method: 'POST' as const,
        path: `/onboarding/practice/${clinicId}/members/invite`,
        body,
      }),
      // Refresh every Practice_Member subscriber (User_Management_Page, the
      // UserAssigneeDropdown on AlarmEditor)
      // immediately after a successful invite — without this, callers had to
      // manually reload to see the new Pending row.
      invalidatesTags: ['PracticeMembers'],
    }),
    acceptInvite: builder.mutation<
      { clinicId: string; role: string; status: string },
      { inviteToken: string; email?: string; name?: string }
    >({
      query: (body) => ({
        method: 'POST' as const,
        path: '/onboarding/practice/accept-invite',
        body,
      }),
    }),
    /**
     * Durable, server-side invite reconciliation. When an authenticated user
     * has no membership, the backend matches a pending invite by the caller's
     * Cognito-VERIFIED email and activates it (assigns role + membership).
     * Idempotent no-op when a membership already exists. This is the safety net
     * for the client-token accept flow, which can silently fail to run (token
     * lost across the signup → verify → login journey, or a different
     * browser/device).
     */
    reconcileMembership: builder.mutation<
      { membership: PracticeMembership | null; reconciled: boolean },
      void
    >({
      query: () => ({
        method: 'POST' as const,
        path: '/onboarding/practice/reconcile-membership',
      }),
    }),
    resendInvite: builder.mutation<
      { message: string; memberId: string },
      { clinicId: string; memberId: string }
    >({
      query: ({ clinicId, memberId }) => ({
        method: 'POST' as const,
        path: `/onboarding/practice/${clinicId}/members/${memberId}/resend-invite`,
      }),
      invalidatesTags: ['PracticeMembers'],
    }),
    getPendingPractices: builder.query<PendingPracticesResponse, void>({
      query: () => ({
        method: 'GET' as const,
        path: '/onboarding/admin/practices/pending',
      }),
      providesTags: ['PendingPractices'],
    }),
    updatePracticeStatus: builder.mutation<UpdatePracticeStatusResponse, UpdatePracticeStatusRequest>({
      query: ({ clinicId, ...body }) => ({
        method: 'PUT' as const,
        path: `/onboarding/admin/practices/${clinicId}/status`,
        body,
      }),
      invalidatesTags: ['PendingPractices'],
    }),
    // --- Role Management endpoints ---
    getRoles: builder.query<RolesResponse, void>({
      query: () => ({
        method: 'GET' as const,
        path: '/onboarding/roles',
      }),
      providesTags: ['Roles'],
    }),
    getAssignableRoles: builder.query<AssignableRolesResponse, string>({
      query: (clinicId) => ({
        method: 'GET' as const,
        path: `/onboarding/practice/${clinicId}/assignable-roles`,
      }),
    }),
    assignRole: builder.mutation<{ member: PracticeMember }, AssignRoleRequest>({
      query: ({ clinicId, userId, ...body }) => ({
        method: 'PUT' as const,
        path: `/onboarding/practice/${clinicId}/members/${userId}/role`,
        body,
      }),
    }),
    getAdminRoles: builder.query<RolesResponse, void>({
      query: () => ({
        method: 'GET' as const,
        path: '/onboarding/admin/roles',
      }),
      providesTags: ['Roles'],
    }),
    createAdminRole: builder.mutation<{ role: RoleDefinition }, AdminCreateRoleRequest>({
      query: (body) => ({
        method: 'POST' as const,
        path: '/onboarding/admin/roles',
        body,
      }),
      invalidatesTags: ['Roles'],
    }),
    updateAdminRole: builder.mutation<{ role: RoleDefinition }, AdminUpdateRoleRequest>({
      query: ({ roleId, ...body }) => ({
        method: 'PUT' as const,
        path: `/onboarding/admin/roles/${roleId}`,
        body,
      }),
      invalidatesTags: ['Roles'],
    }),
    deleteAdminRole: builder.mutation<{ message: string }, string>({
      query: (roleId) => ({
        method: 'DELETE' as const,
        path: `/onboarding/admin/roles/${roleId}`,
      }),
      invalidatesTags: ['Roles'],
    }),
    // --- Group Management endpoints ---
    getGroups: builder.query<Group[], GetGroupsRequest>({
      query: ({ clinicId, includeArchived }) => {
        const params = new URLSearchParams();
        if (includeArchived) params.set('includeArchived', 'true');
        const qs = params.toString();
        return {
          method: 'GET' as const,
          path: `/onboarding/practice/${encodeURIComponent(clinicId)}/groups${qs ? `?${qs}` : ''}`,
        };
      },
      transformResponse: (r: { groups: Group[] } | Group[]) =>
        Array.isArray(r) ? r : (r?.groups ?? []),
      providesTags: ['Groups'],
    }),
    getGroupMembers: builder.query<string[], GetGroupMembersRequest>({
      query: ({ clinicId, groupId }) => ({
        method: 'GET' as const,
        path: `/onboarding/practice/${encodeURIComponent(clinicId)}/groups/${encodeURIComponent(groupId)}/members`,
      }),
      // The endpoint returns `{ members: [...userId] }` — a list of the group's
      // active member userIds. Coerce both the `{ members }` envelope and a bare
      // array (and, defensively, any `{ userId }` objects) to a `string[]` of
      // userIds; the Groups tab resolves names from the practice member list.
      transformResponse: (
        r:
          | { members?: Array<string | { userId?: string }> }
          | Array<string | { userId?: string }>,
      ): string[] => {
        const arr = Array.isArray(r) ? r : (r?.members ?? []);
        return arr
          .map((m) => (typeof m === 'string' ? m : m?.userId))
          .filter((id): id is string => typeof id === 'string' && id.length > 0);
      },
      providesTags: ['Groups'],
    }),
    createGroup: builder.mutation<Group, CreateGroupRequest>({
      query: ({ clinicId, ...body }) => ({
        method: 'POST' as const,
        path: `/onboarding/practice/${encodeURIComponent(clinicId)}/groups`,
        body,
      }),
      transformResponse: (r: { group: Group } | Group) =>
        (r && typeof r === 'object' && 'group' in r ? r.group : r) as Group,
      invalidatesTags: ['Groups'],
    }),
    updateGroup: builder.mutation<Group, UpdateGroupRequest>({
      query: ({ clinicId, groupId, ...body }) => ({
        method: 'PUT' as const,
        path: `/onboarding/practice/${encodeURIComponent(clinicId)}/groups/${encodeURIComponent(groupId)}`,
        body,
      }),
      transformResponse: (r: { group: Group } | Group) =>
        (r && typeof r === 'object' && 'group' in r ? r.group : r) as Group,
      invalidatesTags: ['Groups'],
    }),
    archiveGroup: builder.mutation<Group, ArchiveGroupRequest>({
      query: ({ clinicId, groupId }) => ({
        method: 'POST' as const,
        path: `/onboarding/practice/${encodeURIComponent(clinicId)}/groups/${encodeURIComponent(groupId)}/archive`,
      }),
      transformResponse: (r: { group: Group } | Group) =>
        (r && typeof r === 'object' && 'group' in r ? r.group : r) as Group,
      invalidatesTags: ['Groups'],
    }),
    assignGroupMember: builder.mutation<{ ok: boolean }, GroupMembershipRequest>({
      query: ({ clinicId, groupId, userId }) => ({
        method: 'PUT' as const,
        path: `/onboarding/practice/${encodeURIComponent(clinicId)}/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`,
      }),
      invalidatesTags: ['Groups'],
    }),
    unassignGroupMember: builder.mutation<{ ok: boolean }, GroupMembershipRequest>({
      query: ({ clinicId, groupId, userId }) => ({
        method: 'DELETE' as const,
        path: `/onboarding/practice/${encodeURIComponent(clinicId)}/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`,
      }),
      invalidatesTags: ['Groups'],
    }),
    // --- Logo Upload endpoints ---
    requestLogoPresignedUrl: builder.mutation<LogoPresignResponse, LogoPresignRequest>({
      query: ({ clinicId, ...body }) => ({
        method: 'POST' as const,
        path: `/onboarding/practice/${clinicId}/logo/presign`,
        body,
      }),
    }),
    confirmLogoUpload: builder.mutation<LogoConfirmResponse, LogoConfirmRequest>({
      query: ({ clinicId, ...body }) => ({
        method: 'POST' as const,
        path: `/onboarding/practice/${clinicId}/logo/confirm`,
        body,
      }),
    }),
    deleteLogo: builder.mutation<{ message: string }, string>({
      query: (clinicId) => ({
        method: 'DELETE' as const,
        path: `/onboarding/practice/${clinicId}/logo`,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPracticeQuery,
  useGetPracticeLocationsQuery,
  useCreatePracticeMutation,
  useUpdatePracticeMutation,
  useAddLocationMutation,
  useUpdateLocationMutation,
  usePracticeLookupMutation,
  useJoinPracticeMutation,
  useGetMyMembershipQuery,
  useGetPracticeMembersQuery,
  useUpdateProviderMappingMutation,
  useUpdatePracticeMemberMutation,
  useInvitePracticeMemberMutation,
  useAcceptInviteMutation,
  useReconcileMembershipMutation,
  useResendInviteMutation,
  useGetPendingPracticesQuery,
  useUpdatePracticeStatusMutation,
  useGetRolesQuery,
  useGetAssignableRolesQuery,
  useAssignRoleMutation,
  useGetAdminRolesQuery,
  useCreateAdminRoleMutation,
  useUpdateAdminRoleMutation,
  useDeleteAdminRoleMutation,
  useGetGroupsQuery,
  useLazyGetGroupsQuery,
  useGetGroupMembersQuery,
  useLazyGetGroupMembersQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useArchiveGroupMutation,
  useAssignGroupMemberMutation,
  useUnassignGroupMemberMutation,
  useRequestLogoPresignedUrlMutation,
  useConfirmLogoUploadMutation,
  useDeleteLogoMutation,
} = onboardingApi;
